<div class="notification-center relative" data-testid="notification-center">
  <!-- Notification Bell Icon -->
  <div class="relative">
    <app-button
      variant="ghost"
      size="sm"
      data-testid="notification-bell"
      [class.animate-pulse]="hasUnreadCritical()"
      (clicked)="togglePanel($event)"
      [uiTooltip]="'Notifications'"
      [attr.aria-label]="'Notifications' + (unreadCount() > 0 ? '. ' + unreadCount() + ' unread' : '')">
      <ui-icon name="bell" size="md" aria-hidden="true" />
    </app-button>
    @if (unreadCount() > 0) {
      <ui-badge
        data-testid="notification-badge"
        [value]="unreadCount() > 99 ? '99+' : unreadCount()"
        [variant]="hasUnreadCritical() ? 'danger' : 'warning'"
        class="absolute -top-1 -right-1"
        [attr.aria-label]="unreadCount() + ' unread notifications'">
      </ui-badge>
    }
  </div>

  <!-- Notification Panel Dropdown -->
  @if (panelOpen()) {
    <div class="notification-panel absolute right-0 top-full mt-2 w-[450px] max-h-[600px] bg-bb-bg-secondary border border-bb-border rounded-lg shadow-xl z-dropdown"
         data-testid="notification-panel"
         role="dialog"
         aria-label="Notification Center">
      <div class="notification-panel-content p-4">
        <!-- Header -->
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-bb-text-primary flex items-center gap-2">
            <ui-icon name="bell" size="md" aria-hidden="true" />
            Notifications
            @if (unreadCount() > 0) {
              <ui-badge [value]="unreadCount()" variant="warning"></ui-badge>
            }
          </h3>
          <div class="flex gap-1">
            <app-button
              variant="ghost"
              size="sm"
              data-testid="mark-all-read-btn"
              [uiTooltip]="'Mark all as read'"
              (clicked)="markAllAsRead()"
              [disabled]="unreadCount() === 0">
              <ui-icon name="check-circle" size="sm" aria-hidden="true" />
            </app-button>
            <app-button
              variant="ghost"
              size="sm"
              data-testid="notification-settings-btn"
              [uiTooltip]="'Settings'"
              (clicked)="toggleSettings()">
              <ui-icon name="cog" size="sm" aria-hidden="true" />
            </app-button>
            <app-button
              variant="ghost"
              size="sm"
              data-testid="clear-all-btn"
              [uiTooltip]="'Clear all'"
              (clicked)="confirmClearAll()"
              [disabled]="alerts().length === 0">
              <ui-icon name="trash" size="sm" class="text-bb-accent-red" aria-hidden="true" />
            </app-button>
            <app-button
              variant="ghost"
              size="sm"
              data-testid="close-panel-btn"
              [uiTooltip]="'Close'"
              (clicked)="closePanel()">
              <ui-icon name="times" size="sm" aria-hidden="true" />
            </app-button>
          </div>
        </div>

        <!-- Settings Panel -->
        @if (showSettings()) {
          <div class="settings-panel bg-bb-bg-tertiary rounded-lg p-3 mb-4 border border-bb-border" data-testid="notification-settings-panel">
            <h4 class="text-sm font-semibold text-bb-text-primary mb-3">Notification Settings</h4>

            <p role="status" class="text-sm text-bb-text-secondary mb-3">
              @switch (settingsStatus()) {
                @case ('loading') { Loading saved preferences… }
                @case ('saving') { Saving preferences… }
                @case ('synced') { Preferences saved }
                @case ('error') { Preferences could not be saved or loaded. }
              }
            </p>
            @if (settingsStatus() === 'error') {
              <app-button variant="ghost" size="sm" (clicked)="retrySettings($event)">Reload saved preferences</app-button>
            }
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <label class="text-sm text-bb-text-secondary">Sound Alerts</label>
                <ui-toggle
                  ariaLabel="Sound alerts"
                  [disabled]="!settingsReady()"
                  [(ngModel)]="soundEnabled"
                  (ngModelChange)="toggleSound()">
                </ui-toggle>
              </div>

              <div class="flex items-center justify-between">
                <label class="text-sm text-bb-text-secondary">Desktop Notifications</label>
                <ui-toggle
                  ariaLabel="Desktop notifications"
                  [disabled]="!settingsReady()"
                  [(ngModel)]="desktopEnabled"
                  (ngModelChange)="updateSettings()">
                </ui-toggle>
              </div>

              <div class="flex items-center justify-between">
                <label class="text-sm text-bb-text-secondary">Toast Messages</label>
                <ui-toggle
                  ariaLabel="Toast messages"
                  [disabled]="!settingsReady()"
                  [(ngModel)]="toastEnabled"
                  (ngModelChange)="updateSettings()">
                </ui-toggle>
              </div>

              @if (soundEnabled()) {
                <div>
                  <label class="text-sm text-bb-text-secondary block mb-1">Volume</label>
                  <ui-slider
                    ariaLabel="Notification volume"
                  [disabled]="!settingsReady()"
                    [(ngModel)]="soundVolume"
                    [min]="0"
                    [max]="100"
                    [step]="10"
                    (ngModelChange)="updateSettings()"
                    class="w-full">
                  </ui-slider>
                </div>
              }
            </div>

            <app-button
              variant="ghost"
              size="sm"
              class="mt-3"
              (clicked)="closeSettings($event)">
              Close Settings
            </app-button>
          </div>
        }

        <!-- Tabs -->
        <ui-tabs [(activeIndex)]="activeTab" data-testid="notification-tabs">
          <!-- All Notifications Tab -->
          <ui-tab label="All notifications">
            <ng-template #tabHeader>
              <span class="flex items-center gap-2">
                All
                @if (alerts().length > 0) {
                  <ui-badge [value]="alerts().length" variant="default"></ui-badge>
                }
              </span>
            </ng-template>

            <div class="overflow-y-auto max-h-[400px]" role="region" aria-label="Notifications list">
              @if (alerts().length === 0) {
                <div class="text-center py-8 text-bb-text-secondary" role="status">
                  <ui-icon name="inbox" size="3xl" class="mx-auto block mb-2 text-bb-text-secondary" aria-hidden="true" />
                  <p>No notifications</p>
                </div>
              } @else {
                <div class="space-y-2" role="list" aria-label="All notifications">
                  @for (alert of paginatedAlerts(); track alert.id) {
                    <article
                      class="notification-item p-3 rounded-lg cursor-pointer border border-transparent hover:border-bb-border transition-colors"
                      data-testid="notification-item"
                      [attr.data-severity]="alert.severity"
                      [attr.data-read]="alert.read"
                      [attr.data-id]="alert.id"
                      [class.bg-bb-bg-tertiary]="!alert.read"
                      [class.border-l-4]="alert.severity === 'critical'"
                      [class.border-l-bb-accent-red]="alert.severity === 'critical'"
                      role="listitem"
                      [attr.aria-label]="alert.severity + ' notification: ' + alert.title"
                      tabindex="0"
                      (click)="handleAlertClick(alert)"
                      (keydown.enter)="handleAlertClick(alert)"
                      (keydown.space)="handleAlertClick(alert); $event.preventDefault()">
                      <div class="flex justify-between items-start mb-2">
                        <div class="flex items-center gap-2">
                          @switch (alert.severity) {
                            @case ('critical') {
                              <ui-icon name="exclamation-circle" size="md" class="text-bb-accent-red" aria-hidden="true" />
                            }
                            @case ('error') {
                              <ui-icon name="times-circle" size="md" class="text-bb-accent-red" aria-hidden="true" />
                            }
                            @case ('warning') {
                              <ui-icon name="exclamation-triangle" size="md" class="text-bb-accent-amber" aria-hidden="true" />
                            }
                            @case ('success') {
                              <ui-icon name="check-circle" size="md" class="text-bb-accent-green" aria-hidden="true" />
                            }
                            @default {
                              <ui-icon name="info-circle" size="md" class="text-bb-accent-blue" aria-hidden="true" />
                            }
                          }
                          <span class="font-medium text-bb-text-primary">{{ alert.title }}</span>
                          @if (!alert.read) {
                            <span class="w-2 h-2 bg-bb-accent-blue rounded-full" aria-label="Unread" role="status"></span>
                          }
                        </div>
                        <div class="flex items-center gap-2">
                          <ui-chip
                            [label]="alert.category"
                            [variant]="getChipVariant(alert.category)"
                            size="sm"
                            [attr.aria-label]="'Category: ' + alert.category">
                          </ui-chip>
                          <app-button
                            variant="ghost"
                            size="sm"
                            [attr.aria-label]="'Remove notification: ' + alert.title"
                            (clicked)="removeAlert(alert, $event)">
                            <ui-icon name="times" size="sm" aria-hidden="true" />
                          </app-button>
                        </div>
                      </div>

                      <p class="text-sm text-bb-text-secondary mb-2">
                        {{ alert.message }}
                      </p>

                      @if (alert.actionable && alert.actions) {
                        <div class="flex gap-2 mb-2" role="group" [attr.aria-label]="'Actions for ' + alert.title">
                          @for (action of alert.actions; track action.label) {
                            <app-button
                              [variant]="getActionVariant(action)"
                              size="sm"
                              [attr.aria-label]="action.label + ' for ' + alert.title"
                              (clicked)="executeAction(alert, action, $event)">
                              {{ action.label }}
                            </app-button>
                          }
                        </div>
                      }

                      <p class="text-xs text-bb-text-secondary">
                        <time [attr.datetime]="alert.timestamp.toISOString()">
                          {{ getRelativeTime(alert.timestamp) }}
                        </time>
                      </p>
                    </article>
                  }
                </div>

                @if (alerts().length > pageSize) {
                  <div class="flex justify-center items-center mt-4 gap-2">
                    <app-button
                      variant="ghost"
                      size="sm"
                      ariaLabel="Previous page"
                      (clicked)="previousPage()"
                      [disabled]="currentPage() === 0">
                      <ui-icon name="chevron-left" size="sm" aria-hidden="true" />
                    </app-button>
                    <span class="px-3 py-1 text-sm text-bb-text-secondary">
                      {{ currentPage() + 1 }} / {{ totalPages() }}
                    </span>
                    <app-button
                      variant="ghost"
                      size="sm"
                      ariaLabel="Next page"
                      (clicked)="nextPage()"
                      [disabled]="currentPage() >= totalPages() - 1">
                      <ui-icon name="chevron-right" size="sm" aria-hidden="true" />
                    </app-button>
                  </div>
                }
              }
            </div>
          </ui-tab>

          <!-- Critical Alerts Tab -->
          <ui-tab label="Critical notifications">
            <ng-template #tabHeader>
              <span class="flex items-center gap-2">
                Critical
                @if (criticalAlerts().length > 0) {
                  <ui-badge [value]="criticalAlerts().length" variant="danger"></ui-badge>
                }
              </span>
            </ng-template>

            <div class="overflow-y-auto max-h-[400px]" role="region" aria-label="Critical alerts list">
              @if (criticalAlerts().length === 0) {
                <div class="text-center py-8 text-bb-text-secondary" role="status">
                  <ui-icon name="check-circle" size="3xl" class="mx-auto block mb-2 text-bb-accent-green" aria-hidden="true" />
                  <p>No critical alerts</p>
                </div>
              } @else {
                <div class="space-y-2" role="list" aria-label="Critical notifications">
                  @for (alert of criticalAlerts(); track alert.id) {
                    <article
                      class="notification-item p-3 rounded-lg cursor-pointer border-l-4 border-l-bb-accent-red bg-bb-accent-red/5"
                      data-testid="notification-item"
                      [attr.data-severity]="alert.severity"
                      [attr.data-read]="alert.read"
                      [attr.data-id]="alert.id"
                      role="listitem"
                      [attr.aria-label]="'Critical alert: ' + alert.title"
                      tabindex="0"
                      (click)="handleAlertClick(alert)"
                      (keydown.enter)="handleAlertClick(alert)"
                      (keydown.space)="handleAlertClick(alert); $event.preventDefault()">
                      <div class="flex justify-between items-start mb-2">
                        <div class="flex items-center gap-2">
                          <ui-icon name="exclamation-circle" size="md" class="text-bb-accent-red" [decorative]="false" aria-label="Critical" />
                          <span class="font-medium text-bb-accent-red">{{ alert.title }}</span>
                          @if (!alert.read) {
                            <span class="w-2 h-2 bg-bb-accent-red rounded-full animate-pulse" aria-label="Unread critical alert" role="status"></span>
                          }
                        </div>
                        <app-button
                          variant="ghost"
                          size="sm"
                          [attr.aria-label]="'Remove critical alert: ' + alert.title"
                          (clicked)="removeAlert(alert, $event)">
                          <ui-icon name="times" size="sm" aria-hidden="true" />
                        </app-button>
                      </div>

                      <p class="text-sm text-bb-text-secondary mb-2">
                        {{ alert.message }}
                      </p>

                      @if (alert.actionable && alert.actions) {
                        <div class="flex gap-2 mb-2" role="group" [attr.aria-label]="'Actions for ' + alert.title">
                          @for (action of alert.actions; track action.label) {
                            <app-button
                              [variant]="getActionVariant(action)"
                              size="sm"
                              [attr.aria-label]="action.label + ' for ' + alert.title"
                              (clicked)="executeAction(alert, action, $event)">
                              {{ action.label }}
                            </app-button>
                          }
                        </div>
                      }

                      <p class="text-xs text-bb-text-secondary">
                        <time [attr.datetime]="alert.timestamp.toISOString()">
                          {{ getRelativeTime(alert.timestamp) }}
                        </time>
                      </p>
                    </article>
                  }
                </div>
              }
            </div>
          </ui-tab>

          <!-- Stats Tab -->
          <ui-tab header="Stats">
            <div class="stats-content p-4">
              <div class="grid grid-cols-2 gap-4 mb-4">
                <div class="stat-card bg-bb-bg-tertiary rounded-lg p-3">
                  <p class="text-sm text-bb-text-secondary">Total Alerts</p>
                  <p class="text-2xl font-bold text-bb-text-primary">{{ stats().total }}</p>
                </div>
                <div class="stat-card bg-bb-bg-tertiary rounded-lg p-3">
                  <p class="text-sm text-bb-text-secondary">Unread</p>
                  <p class="text-2xl font-bold text-bb-accent-amber">{{ stats().unread }}</p>
                </div>
                <div class="stat-card bg-bb-bg-tertiary rounded-lg p-3">
                  <p class="text-sm text-bb-text-secondary">Today</p>
                  <p class="text-2xl font-bold text-bb-text-primary">{{ stats().todayCount }}</p>
                </div>
                <div class="stat-card bg-bb-bg-tertiary rounded-lg p-3">
                  <p class="text-sm text-bb-text-secondary">Critical</p>
                  <p class="text-2xl font-bold text-bb-accent-red">{{ criticalAlerts().length }}</p>
                </div>
              </div>

              <ui-divider></ui-divider>

              <div class="mb-4 mt-4">
                <h4 class="text-sm font-semibold text-bb-text-primary mb-2">By Category</h4>
                <div class="space-y-2">
                  @for (category of getCategories(); track category) {
                    @if (stats().byCategory[category]) {
                      <div class="flex justify-between items-center">
                        <ui-chip
                          [label]="category"
                          [variant]="getChipVariant(category)"
                          size="sm">
                        </ui-chip>
                        <span class="text-sm font-medium text-bb-text-primary">{{ stats().byCategory[category] }}</span>
                      </div>
                    }
                  }
                </div>
              </div>

              <ui-divider></ui-divider>

              <div class="mt-4">
                <h4 class="text-sm font-semibold text-bb-text-primary mb-2">By Severity</h4>
                <div class="space-y-2">
                  @for (severity of getSeverities(); track severity) {
                    @if (stats().bySeverity[severity]) {
                      <div class="flex justify-between items-center">
                        <div class="flex items-center gap-2">
                          @switch (severity) {
                            @case ('critical') {
                              <ui-icon name="ban" size="sm" class="text-bb-accent-red" aria-hidden="true" />
                            }
                            @case ('error') {
                              <ui-icon name="times-circle" size="sm" class="text-bb-accent-red" aria-hidden="true" />
                            }
                            @case ('warning') {
                              <ui-icon name="exclamation-triangle" size="sm" class="text-bb-accent-amber" aria-hidden="true" />
                            }
                            @case ('success') {
                              <ui-icon name="check-circle" size="sm" class="text-bb-accent-green" aria-hidden="true" />
                            }
                            @default {
                              <ui-icon name="info-circle" size="sm" class="text-bb-accent-blue" aria-hidden="true" />
                            }
                          }
                          <span class="text-sm capitalize text-bb-text-primary">{{ severity }}</span>
                        </div>
                        <span class="text-sm font-medium text-bb-text-primary">{{ stats().bySeverity[severity] }}</span>
                      </div>
                    }
                  }
                </div>
              </div>
            </div>
          </ui-tab>
        </ui-tabs>
      </div>
    </div>
  }

  <!-- Confirm Clear Dialog -->
  <ui-modal
    data-testid="confirm-clear-modal"
    [visible]="confirmDialogOpen()"
    title="Clear Notifications"
    size="sm"
    (close)="cancelClearAll()">
    <div class="flex items-center gap-3 mb-4">
      <ui-icon name="exclamation-triangle" size="xl" class="text-bb-accent-amber" aria-hidden="true" />
      <p class="text-bb-text-secondary">Are you sure you want to clear all notifications?</p>
    </div>
    <div class="flex justify-end gap-2">
      <app-button variant="outline" (clicked)="cancelClearAll()">Cancel</app-button>
      <app-button variant="danger" (clicked)="executeClearAll()">Clear All</app-button>
    </div>
  </ui-modal>
</div>
